import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Clock } from 'lucide-react';
import { Badge } from '@voll-intelligence/ui-kit/ui/badge';
import type { ApprovalsTripCopy } from '../approvals_copy.js';
import { useApprovalsEnv } from './approvals_env.js';

/**
 * O relógio da tela — o tick que faz o countdown RODAR. O rótulo mostra hora e
 * minuto (sem segundos), mas o tick segue por segundo para virar o minuto na
 * hora certa. `expiresAt` é instante ISO do espelho; o resto é aritmética sobre
 * `Date.now()`.
 */
const NowMs = createContext(0);

export function useNowMs() {
  return useContext(NowMs);
}

export function ClockProvider({ children }: { children: ReactNode }) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => {
      setNowMs(Date.now());
    }, 1_000);
    return () => {
      clearInterval(id);
    };
  }, []);
  return <NowMs.Provider value={nowMs}>{children}</NowMs.Provider>;
}

/** A régua da validação: neutro >24h, âmbar ≤24h, destructive ≤30min. */
export const NEAR_EXPIRY_MS = 24 * 3_600_000;
export const CRITICAL_EXPIRY_MS = 30 * 60_000;

function expiryLabel(totalSeconds: number, copy: ApprovalsTripCopy): string {
  if (totalSeconds <= 0) return copy.expired;
  if (totalSeconds >= 48 * 3600) {
    return copy.expiresInDays(Math.round(totalSeconds / 86_400));
  }
  // Minuto começado conta inteiro: com prazo vivo o chip nunca diz "0min".
  const totalMinutes = Math.ceil(totalSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) {
    return copy.expiresInHours(hours, String(minutes).padStart(2, '0'));
  }
  return copy.expiresInMinutes(minutes);
}

/** Chip do prazo — PÍLULA viva: countdown em horas e minutos com a régua acima, no
 *  canto do cabeçalho da ficha. É o mesmo `Badge soft` do chip de decisão. */
export function ExpiryChip({ expiresAtMs }: { expiresAtMs: number }) {
  const nowMs = useNowMs();
  const { copy } = useApprovalsEnv();
  const msLeft = expiresAtMs - nowMs;
  const color =
    msLeft <= CRITICAL_EXPIRY_MS ? 'danger' : msLeft <= NEAR_EXPIRY_MS ? 'warning' : 'neutral';
  return (
    <Badge
      variant="soft"
      color={color}
      className="h-5.5 gap-1 px-2 font-medium whitespace-nowrap tabular-nums"
    >
      <Clock aria-hidden />
      {expiryLabel(Math.floor(msLeft / 1000), copy.trip)}
    </Badge>
  );
}
